import type { ReactNode } from 'react';
import { Inbox, Search } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { Input } from '@/shared/ui/input';
import { Checkbox } from '@/shared/ui/checkbox';
import { Label } from '@/shared/ui/label';
import { Skeleton } from '@/shared/ui/skeleton';
import { EmptyState } from '@/shared/components/empty-state';

interface SelectableListLayoutProps {
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  /**
   * Conteúdo ao lado da busca — só mobile (ex.: botão Filtros).
   * No `sm+` o desktop permanece igual; o caller deve ocultar este slot com `sm:hidden`.
   */
  searchAside?: ReactNode;
  /** Slot extra na toolbar (ex.: SortSelect). Ignorado quando `filtersRow` está definido. */
  toolbarExtra?: ReactNode;
  /** Segunda linha da toolbar — grade de filtros (ex.: leads / campanhas automatizadas). */
  filtersRow?: ReactNode;
  selectAllId: string;
  itemCount: number;
  allOnPageSelected: boolean;
  someOnPageSelected: boolean;
  onToggleAllOnPage: () => void;
  /** Oculta o checkbox “selecionar página”. */
  hideSelection?: boolean;
  isLoading?: boolean;
  isEmpty?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Espaço extra no rodapé para a barra flutuante de ações. */
  withFloatingBar?: boolean;
  /** Quando fornecido, usa este wrapper em vez de `<ul>` (ex.: Reorder.Group para DnD). */
  listWrapper?: (children: ReactNode) => ReactNode;
}

/** Toolbar + lista de cards selecionáveis (busca, selecionar página, loading, vazio). */
export function SelectableListLayout({
  search,
  onSearchChange,
  searchPlaceholder = 'Buscar…',
  searchAside,
  toolbarExtra,
  filtersRow,
  selectAllId,
  itemCount,
  allOnPageSelected,
  someOnPageSelected,
  onToggleAllOnPage,
  hideSelection = false,
  isLoading,
  isEmpty,
  emptyTitle = 'Nenhum registro',
  emptyDescription = 'Tente ajustar a busca ou adicione um novo item.',
  children,
  footer,
  withFloatingBar = true,
  listWrapper,
}: SelectableListLayoutProps) {
  const list = listWrapper
    ? listWrapper(children)
    : <ul className="space-y-2.5">{children}</ul>;

  const showSelectAll = itemCount > 0 && !isLoading && !hideSelection;
  const hasFiltersRow = Boolean(filtersRow);
  const hasToolbarExtra = Boolean(!hasFiltersRow && toolbarExtra);

  const selectAllInner = showSelectAll ? (
    <>
      <Checkbox
        id={selectAllId}
        checked={allOnPageSelected ? true : someOnPageSelected ? 'indeterminate' : false}
        onCheckedChange={onToggleAllOnPage}
      />
      <Label
        htmlFor={selectAllId}
        className="min-w-0 cursor-pointer truncate text-sm text-muted-foreground"
      >
        Selecionar página ({itemCount})
      </Label>
    </>
  ) : null;

  return (
    <div className={withFloatingBar ? 'space-y-4 pb-24' : 'space-y-4'}>
      <div className="space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div
            className={cn(
              'relative min-w-0 w-full sm:max-w-md sm:flex-1',
              searchAside && 'flex items-center gap-2',
            )}
          >
            <div className={cn('relative min-w-0', searchAside ? 'flex-1' : 'w-full')}>
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={searchPlaceholder}
                className="pl-9"
              />
            </div>
            {searchAside ? <div className="shrink-0">{searchAside}</div> : null}
          </div>

          {hasToolbarExtra ? (
            /*
             * Mobile: “selecionar página” + filtro na mesma linha, 50/50, abaixo da busca.
             * Desktop (`sm:contents`): voltam a ser irmãos da busca na mesma toolbar.
             */
            <div className="grid grid-cols-2 items-stretch gap-2 sm:contents">
              {selectAllInner ? (
                <div
                  className={cn(
                    'flex min-w-0 items-center gap-2',
                    'h-10 rounded-lg border border-border/70 bg-foreground/[0.04] px-3',
                    'sm:order-last sm:ml-auto sm:h-auto sm:rounded-none sm:border-0 sm:bg-transparent sm:px-0',
                  )}
                >
                  {selectAllInner}
                </div>
              ) : null}
              <div className="min-w-0 [&_button]:h-10 [&_button]:w-full sm:w-auto sm:[&_button]:w-auto">
                {toolbarExtra}
              </div>
            </div>
          ) : selectAllInner && !hasFiltersRow ? (
            <div className="flex shrink-0 items-center gap-2 sm:ml-auto">{selectAllInner}</div>
          ) : selectAllInner && hasFiltersRow ? (
            /* Com filtersRow: no mobile fica sob a busca; no desktop vai para a linha dos filtros. */
            <div className="flex shrink-0 items-center gap-2 sm:hidden">{selectAllInner}</div>
          ) : null}
        </div>

        {hasFiltersRow ? (
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">{filtersRow}</div>
            {selectAllInner ? (
              <div className="hidden shrink-0 items-center gap-2 sm:flex">{selectAllInner}</div>
            ) : null}
          </div>
        ) : null}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-[72px] rounded-2xl" />
          ))}
        </div>
      ) : isEmpty ? (
        <EmptyState icon={Inbox} title={emptyTitle} description={emptyDescription} />
      ) : list}

      {footer}
    </div>
  );
}
